import {
  getLibraryAdminSession,
  logoutLibraryAdmin,
} from "@libraryadmin/services/auth";
import {
  Search,
  RefreshCw,
  MoreHorizontal,
  Eye,
  ShieldCheck,
  Trash2,
  Users,
} from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@jambushrushti/components/ui/select";
import { Switch } from "@jambushrushti/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@jambushrushti/components/ui/dropdown-menu";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listUsers,
  getUser,
  deleteUser,
  type ManagedUser,
} from "@jambushrushti/services/portalUsers";
import {
  getAdminSession,
  logoutAdmin,
} from "@jambushrushti/services/adminAuth";
import { useToast } from "@jambushrushti/hooks/use-toast";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Label } from "@jambushrushti/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@jambushrushti/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@jambushrushti/components/ui/dialog";
import {
  AdminViewToggle,
  useAdminViewMode,
} from "@jambushrushti/components/admin/AdminViewToggle";

const labels = {
  cms: "CMS",
  library: "Library",
  admin: "Admin",
  unknown: "Unknown",
};
export function UsersDirectory({
  portal = "cms",
}: {
  portal?: "cms" | "library";
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [inactive, setInactive] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<ManagedUser | null>(null);
  const [action, setAction] = useState<"view" | "delete" | null>(null);
  const [viewMode, setViewMode] = useAdminViewMode();
  const query = useQuery({
    queryKey: ["portal-users", portal, inactive],
    queryFn: () => listUsers(inactive, portal),
    retry: false,
  });
  useEffect(() => {
    if (query.error) {
      toast({
        title: "Unable to load users",
        description: query.error.message,
        variant: "destructive",
      });
    }
  }, [query.error, toast]);
  const showError = (error: unknown) =>
    toast({
      title: "Unable to complete request",
      description: error instanceof Error ? error.message : "Please try again.",
      variant: "destructive",
    });
  const open = async (id: string, next: "view" | "delete") => {
    if (busy) {
      return;
    }
    setBusy(true);
    try {
      const user = await getUser(id, portal);
      setSelected(user);
      setAction(next);
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    if (!selected || busy || action !== "delete") {
      return;
    }
    setBusy(true);
    try {
      await deleteUser(selected.id, portal);
      toast({
        title: "User deleted",
        description: selected.email,
      });
      setAction(null);
      void queryClient.invalidateQueries({ queryKey: ["portal-users"] });
      if (
        portal === "library"
          ? selected.email === getLibraryAdminSession()?.user.email
          : selected.id === getAdminSession()?.user.id
      ) {
        if (portal === "library") {
          logoutLibraryAdmin();
        } else {
          logoutAdmin();
        }
        window.location.assign(
          portal === "library" ? "/vault-library-admin" : "/vault-cms-admin"
        );
      }
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };
  const rows = (query.data ?? []).filter(
    user =>
      user.email.toLowerCase().includes(search.toLowerCase()) &&
      (filter === "all" || user.role === filter)
  );
  return (
    <div className="overflow-hidden rounded-2xl border border-umber-200/80 bg-card shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-umber-100 px-5 py-5 sm:px-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-heritage-50 text-heritage-700">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-umber-900">
              User directory
            </h2>
            <p className="mt-0.5 text-xs text-umber-500">
              Accounts and portal access
            </p>
          </div>
        </div>
        {query.isSuccess && (
          <span className="rounded-full bg-umber-100 px-3 py-1 text-xs font-medium text-umber-600">
            {query.data.length} users
          </span>
        )}
      </div>
      <div className="flex flex-col gap-3 border-b border-umber-100 bg-umber-50/40 px-5 py-4 sm:px-6 lg:flex-row lg:items-center">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-umber-400" />
          <Input
            aria-label="Search by email"
            placeholder="Search users by email..."
            className="h-10 rounded-xl border-umber-200 bg-card pl-10 shadow-none"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger
              aria-label="Filter role"
              className="h-10 w-36 rounded-xl border-umber-200 bg-card"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value={portal}>
                {portal === "library" ? "Library" : "CMS"}
              </SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2.5 px-1">
            <Switch
              id="include-inactive-users"
              checked={inactive}
              onCheckedChange={setInactive}
            />
            <Label
              htmlFor="include-inactive-users"
              className="cursor-pointer whitespace-nowrap text-xs font-medium text-umber-600"
            >
              Show inactive
            </Label>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 rounded-xl border border-umber-200 bg-card text-umber-500 hover:bg-umber-100"
            aria-label="Refresh users"
            title="Refresh users"
            disabled={query.isFetching}
            onClick={() => {
              void query.refetch();
            }}
          >
            <RefreshCw
              className={query.isFetching ? "h-4 w-4 animate-spin" : "h-4 w-4"}
            />
          </Button>
          <AdminViewToggle value={viewMode} onChange={setViewMode} />
        </div>
      </div>
      {query.isPending ? (
        <p
          role="status"
          className="px-6 py-12 text-center text-sm text-umber-500"
        >
          Loading users...
        </p>
      ) : query.isError ? (
        <p role="alert" className="px-6 py-8 text-sm text-destructive">
          {query.error.message} Use Refresh to retry.
        </p>
      ) : viewMode === "table" ? (
        <>
          <Table>
            <TableHeader>
              <TableRow className="border-umber-100 bg-card hover:bg-card">
                <TableHead className="px-6 text-xs font-medium text-umber-400">
                  USER
                </TableHead>
                <TableHead className="text-xs font-medium text-umber-400">
                  ROLE
                </TableHead>
                <TableHead className="text-xs font-medium text-umber-400">
                  STATUS
                </TableHead>
                <TableHead className="w-20 px-6 text-right text-xs font-medium text-umber-400">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(user => (
                <TableRow
                  key={user.id}
                  className="border-umber-100 transition-colors hover:bg-heritage-50/30"
                >
                  <TableCell className="px-6 py-5">
                    <div className="flex items-center gap-3">
                      <div
                        aria-hidden="true"
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-heritage-100 bg-heritage-50 text-sm font-semibold uppercase text-heritage-800"
                      >
                        {user.email.charAt(0)}
                      </div>
                      <div>
                        <span className="block text-sm font-medium text-umber-800">
                          {user.email}
                        </span>
                        <span className="mt-0.5 block text-xs text-umber-400">
                          {user.role === "admin"
                            ? "CMS & Library access"
                            : user.role === "cms"
                              ? "CMS access"
                              : user.role === "library"
                                ? "Library access"
                                : "Access not specified"}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={
                        "inline-flex items-center rounded-lg px-2.5 py-1 text-xs font-medium ring-1 ring-inset " +
                        (user.role === "admin"
                          ? "bg-heritage-50 text-heritage-800 ring-heritage-200/70"
                          : user.role === "cms"
                            ? "bg-umber-50 text-umber-700 ring-umber-200/70"
                            : user.role === "library"
                              ? "bg-heritage-50 text-heritage-700 ring-heritage-200/70"
                              : "bg-umber-50 text-umber-600 ring-umber-200")
                      }
                    >
                      {labels[user.role]}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span
                      className={
                        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium " +
                        (user.is_active
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-umber-100 text-umber-500")
                      }
                    >
                      <span
                        className={
                          "h-1.5 w-1.5 rounded-full " +
                          (user.is_active ? "bg-emerald-500" : "bg-umber-400")
                        }
                      />
                      {user.is_active === null
                        ? "Not provided"
                        : user.is_active
                          ? "Active"
                          : "Inactive"}
                    </span>
                  </TableCell>
                  <TableCell className="px-6 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 rounded-lg text-umber-400 hover:bg-umber-100 hover:text-umber-700"
                          disabled={busy}
                          aria-label={"Actions for " + user.email}
                        >
                          <MoreHorizontal className="h-5 w-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="w-44 rounded-xl p-1.5"
                      >
                        <DropdownMenuItem
                          onSelect={() => {
                            void open(user.id, "view");
                          }}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          View details
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600 focus:bg-red-50 focus:text-red-700"
                          onSelect={() => {
                            void open(user.id, "delete");
                          }}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete user
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="py-12 text-center">
                    <Users className="mx-auto mb-3 h-7 w-7 text-umber-300" />
                    <p className="font-medium text-umber-700">No users found</p>
                    <p className="mt-1 text-xs text-umber-500">
                      Try another email or adjust your filters.
                    </p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <div className="border-t border-umber-100 bg-umber-50/40 px-6 py-3 text-xs text-umber-500">
            Showing {rows.length} of {query.data.length} users
          </div>
        </>
      ) : (
        <>
          <div className="grid gap-4 p-5 sm:grid-cols-2 xl:grid-cols-3">
            {rows.map(user => (
              <article
                key={user.id}
                className="rounded-xl border border-umber-200 bg-card p-4 transition-shadow hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-heritage-50 font-semibold uppercase text-heritage-800">
                      {user.email.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-umber-900">
                        {user.email}
                      </p>
                      <p className="mt-1 text-xs text-umber-500">
                        {labels[user.role]} access
                      </p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="icon" variant="ghost" disabled={busy}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onSelect={() => void open(user.id, "view")}
                      >
                        <Eye className="mr-2 h-4 w-4" /> View details
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-red-600"
                        onSelect={() => void open(user.id, "delete")}
                      >
                        <Trash2 className="mr-2 h-4 w-4" /> Delete user
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-umber-100 pt-3">
                  <span className="rounded-lg bg-umber-100 px-2.5 py-1 text-xs font-medium text-umber-600">
                    {labels[user.role]}
                  </span>
                  <span
                    className={
                      "inline-flex items-center gap-1.5 text-xs font-medium " +
                      (user.is_active ? "text-emerald-700" : "text-umber-500")
                    }
                  >
                    <span
                      className={
                        "h-1.5 w-1.5 rounded-full " +
                        (user.is_active ? "bg-emerald-500" : "bg-umber-400")
                      }
                    />
                    {user.is_active === null
                      ? "Not provided"
                      : user.is_active
                        ? "Active"
                        : "Inactive"}
                  </span>
                </div>
              </article>
            ))}
            {rows.length === 0 && (
              <div className="col-span-full py-12 text-center">
                <Users className="mx-auto mb-3 h-7 w-7 text-umber-300" />
                <p className="font-medium text-umber-700">No users found</p>
              </div>
            )}
          </div>
          <div className="border-t border-umber-100 bg-umber-50/40 px-6 py-3 text-xs text-umber-500">
            Showing {rows.length} of {query.data.length} users
          </div>
        </>
      )}
      <Dialog
        open={action !== null}
        onOpenChange={value => {
          if (!value && !busy) {
            setAction(null);
          }
        }}
      >
        <DialogContent className="gap-0 overflow-hidden rounded-2xl border-umber-200 bg-card p-0 shadow-2xl sm:max-w-[460px]">
          <DialogHeader className="border-b border-umber-100 px-6 pb-5 pt-6 text-left">
            <DialogTitle className="text-lg font-semibold tracking-tight text-umber-900">
              {action === "delete" ? "Delete user?" : "User details"}
            </DialogTitle>
            <DialogDescription className="break-all text-sm text-umber-500">
              {action === "view"
                ? "Account information and portal permissions."
                : selected?.email}
            </DialogDescription>
          </DialogHeader>
          {action === "view" && selected && (
            <div className="space-y-5 px-6 py-6">
              <div className="flex items-center gap-3.5">
                <div
                  aria-hidden="true"
                  className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-heritage-200/60 bg-heritage-50 text-xl font-semibold uppercase text-heritage-800"
                >
                  {selected.email.charAt(0)}
                </div>
                <div className="min-w-0">
                  <p className="break-all text-sm font-semibold text-umber-900">
                    {selected.email}
                  </p>
                  <p className="mt-1 text-xs text-umber-500">
                    {selected.role === "admin"
                      ? "CMS & Library access"
                      : selected.role === "cms"
                        ? "CMS access"
                        : selected.role === "library"
                          ? "Library access"
                          : "Access not specified"}
                  </p>
                </div>
              </div>
              <dl className="divide-y divide-umber-100 rounded-xl border border-umber-200/70 bg-umber-50/40 px-4">
                <div className="flex items-center justify-between gap-4 py-3.5">
                  <dt className="text-sm text-umber-500">User ID</dt>
                  <dd className="break-all text-right font-mono text-xs font-medium text-umber-700">
                    {selected.id}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-4 py-3.5">
                  <dt className="text-sm text-umber-500">Role</dt>
                  <dd>
                    <span
                      className={
                        "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium ring-1 ring-inset " +
                        (selected.role === "admin"
                          ? "bg-heritage-50 text-heritage-800 ring-heritage-200/70"
                          : selected.role === "cms"
                            ? "bg-umber-50 text-umber-700 ring-umber-200/70"
                            : selected.role === "library"
                              ? "bg-heritage-50 text-heritage-700 ring-heritage-200/70"
                              : "bg-umber-100 text-umber-600 ring-umber-200")
                      }
                    >
                      <ShieldCheck className="h-3.5 w-3.5" />
                      {labels[selected.role]}
                    </span>
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-4 py-3.5">
                  <dt className="text-sm text-umber-500">Account status</dt>
                  <dd>
                    <span
                      className={
                        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium " +
                        (selected.is_active
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-umber-100 text-umber-500")
                      }
                    >
                      <span
                        className={
                          "h-1.5 w-1.5 rounded-full " +
                          (selected.is_active
                            ? "bg-emerald-500"
                            : "bg-umber-400")
                        }
                      />
                      {selected.is_active === null
                        ? "Not provided"
                        : selected.is_active
                          ? "Active"
                          : "Inactive"}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>
          )}
          {action === "delete" && (
            <p className="px-6 py-6 text-sm text-umber-600">
              Confirm deletion of this account to continue.
            </p>
          )}
          <DialogFooter className="border-t border-umber-100 bg-umber-50/60 px-6 py-4">
            <Button
              variant="outline"
              className="min-w-24 rounded-xl border-umber-200 bg-card text-umber-700 shadow-sm hover:border-heritage-300 hover:bg-heritage-50 hover:text-heritage-900"
              disabled={busy}
              onClick={() => setAction(null)}
            >
              {action === "view" ? "Close" : "Cancel"}
            </Button>
            {action !== "view" && (
              <Button
                variant={action === "delete" ? "destructive" : "default"}
                disabled={busy}
                onClick={() => {
                  void save();
                }}
              >
                {busy ? "Deleting..." : "Delete user"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
